/**
 * Os cinco desenhos do jardim: lírio, rosa, girassol, tulipa e cerejeira.
 *
 * No Flutter são cinco `CustomPainter` (`lily_painter.dart` e companhia). Aqui
 * são cinco funções que falam com um pincel, e o `Path`/`Paint` de lá e o
 * contexto 2D do canvas dizem a mesma coisa com outras palavras (`moveTo`,
 * `quadraticCurveTo`, `fill`) — a transcrição é quase linha a linha, e os
 * números todos são os de lá.
 *
 * Ficam no `shared`, e não na tela, porque são dois desenhistas diferentes: o
 * navegador tem canvas e o React Native não tem. `Pincel2D` é o pedaço do
 * contexto 2D que estes desenhos usam de verdade — o navegador entrega o
 * contexto próprio e o celular entrega um pincel que escreve SVG. Duplicar
 * seiscentas linhas de desenho seria garantir que uma flor mude de um lado e
 * não do outro.
 */

/** O `CanvasGradient`, reduzido ao que o vaso pede. */
export interface Degrade {
  addColorStop(parada: number, cor: string): void;
}

/**
 * O contexto 2D visto por estes desenhos. É de propósito menor que o do
 * navegador: o que não está aqui é o que o pincel do celular não precisa saber
 * fazer.
 */
export interface Pincel2D {
  fillStyle: string | Degrade;
  strokeStyle: string;
  lineWidth: number;
  lineCap: 'butt' | 'round' | 'square';
  /** Só o `blur(Npx)` das fases 5. */
  filter: string;

  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cx: number, cy: number, x: number, y: number): void;
  bezierCurveTo(
    c1x: number,
    c1y: number,
    c2x: number,
    c2y: number,
    x: number,
    y: number,
  ): void;
  arc(x: number, y: number, raio: number, de: number, ate: number): void;
  arcTo(x1: number, y1: number, x2: number, y2: number, raio: number): void;
  ellipse(
    x: number,
    y: number,
    raioX: number,
    raioY: number,
    giro: number,
    de: number,
    ate: number,
  ): void;
  fill(): void;
  stroke(): void;
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(angulo: number): void;
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): Degrade;
}

export type Pincel = (ctx: Pincel2D, lado: number, fase: number, respiro: number) => void;

/* ── As peças que os cinco desenhos dividem ─────────────────────────────── */

/**
 * `Rect.fromCenter` + `RRect.fromRectAndRadius`, que é como todos os vasos de
 * lá são feitos. O `x`/`y` daqui é o canto, e não o centro.
 */
function retanguloRedondo(
  ctx: Pincel2D,
  x: number,
  y: number,
  largura: number,
  altura: number,
  raio: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + raio, y);
  ctx.arcTo(x + largura, y, x + largura, y + altura, raio);
  ctx.arcTo(x + largura, y + altura, x, y + altura, raio);
  ctx.arcTo(x, y + altura, x, y, raio);
  ctx.arcTo(x, y, x + largura, y, raio);
  ctx.closePath();
  ctx.fill();
}

/**
 * O vaso: os cinco desenham o mesmo, com outra cor e outro tamanho. O degradê
 * é o `LinearGradient(topLeft, bottomRight)` do Flutter.
 */
function vaso(
  ctx: Pincel2D,
  cx: number,
  cy: number,
  respiro: number,
  largura: number,
  altura: number,
  de: string,
  para: string,
  aro: string,
) {
  const l = largura + respiro * 2;
  const x = cx - l / 2;
  // O centro do vaso é o pé da planta descido meia altura, e por isso o topo
  // do retângulo cai exatamente em `cy`.
  const y = cy;

  const degrade = ctx.createLinearGradient(x, y, x + l, y + altura);
  degrade.addColorStop(0, de);
  degrade.addColorStop(1, para);
  ctx.fillStyle = degrade;
  retanguloRedondo(ctx, x, y, l, altura, 8);

  ctx.fillStyle = aro;
  retanguloRedondo(ctx, x - 4, y - 5, l + 8, 8, 4);
}

/** A pétala que se repete em quase todos: sai do centro, sobe e volta. */
function petala(ctx: Pincel2D, largura: number, altura: number, ponta = altura) {
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(largura, -altura, 0, -ponta);
  ctx.quadraticCurveTo(-largura, -altura, 0, 0);
  ctx.closePath();
  ctx.fill();
}

function circulo(ctx: Pincel2D, x: number, y: number, raio: number) {
  ctx.beginPath();
  ctx.arc(x, y, raio, 0, Math.PI * 2);
  ctx.fill();
}

/** `canvas.drawOval(Rect.fromCenter(...))`. */
function oval(ctx: Pincel2D, x: number, y: number, largura: number, altura: number) {
  ctx.beginPath();
  ctx.ellipse(x, y, largura / 2, altura / 2, 0, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * O `MaskFilter.blur(BlurStyle.normal, sigma)` das fases 5. O `filter` do
 * canvas mede em desvio padrão como o Flutter, então o número passa direto.
 */
function comBrilho(ctx: Pincel2D, sigma: number, desenhar: () => void) {
  ctx.save();
  ctx.filter = `blur(${sigma}px)`;
  desenhar();
  ctx.restore();
}

/** `Color.lerp`, só para o miolo em camadas da rosa. */
function entre(a: [number, number, number], b: [number, number, number], t: number, alfa: number) {
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * t));
  return `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alfa})`;
}

/* ── Lírio (lily_painter.dart) ──────────────────────────────────────────── */

const lirio: Pincel = (ctx, lado, fase, respiro) => {
  const cx = lado / 2;
  const cy = lado * 0.8;

  vaso(ctx, cx, cy, respiro, 60, 40, '#f8bbd0', '#f48fb1', '#f06292');
  if (fase < 1) return;

  const caule = fase * 20 + respiro * 5;
  ctx.strokeStyle = '#81c784';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.quadraticCurveTo(cx + 10, cy - caule / 2, cx, cy - caule);
  ctx.stroke();

  folhaDoLirio(ctx, cx + 5, cy - 10, true);
  if (fase >= 2) folhaDoLirio(ctx, cx - 5, cy - 25, false);

  const topo = cy - caule;

  if (fase === 1) {
    ctx.fillStyle = '#a5d6a7';
    circulo(ctx, cx, topo, 5);
    return;
  }
  if (fase <= 3) {
    ctx.fillStyle = '#f8bbd0';
    oval(ctx, cx, topo, 15, 25);
    return;
  }

  const quantas = fase === 4 ? 3 : 6;
  const largura = fase === 4 ? 20 : 35;
  ctx.fillStyle = 'rgba(244, 143, 177, 0.9)';
  for (let i = 0; i < quantas; i++) {
    ctx.save();
    ctx.translate(cx, topo);
    ctx.rotate(((2 * Math.PI) / quantas) * i);
    if (fase === 5) {
      comBrilho(ctx, 10, () => {
        ctx.fillStyle = 'rgba(252, 228, 236, 0.5)';
        petala(ctx, largura / 2, largura, largura * 1.2);
      });
      ctx.fillStyle = 'rgba(244, 143, 177, 0.9)';
    }
    petala(ctx, largura / 2, largura, largura * 1.2);
    ctx.restore();
  }

  ctx.fillStyle = '#fff59d';
  circulo(ctx, cx, topo, 5);
};

function folhaDoLirio(ctx: Pincel2D, x: number, y: number, direita: boolean) {
  ctx.fillStyle = '#66bb6a';
  ctx.beginPath();
  ctx.moveTo(x, y);
  if (direita) ctx.quadraticCurveTo(x + 20, y - 10, x + 5, y - 20);
  else ctx.quadraticCurveTo(x - 20, y - 10, x - 5, y - 20);
  ctx.closePath();
  ctx.fill();
}

/* ── Rosa (rose_painter.dart) ───────────────────────────────────────────── */

const rosa: Pincel = (ctx, lado, fase, respiro) => {
  const cx = lado / 2;
  const cy = lado * 0.8;

  vaso(ctx, cx, cy, respiro, 60, 40, '#e8b4b8', '#d4899a', '#c07080');
  if (fase < 1) return;

  const caule = fase * 20 + respiro * 5;
  ctx.strokeStyle = '#4caf50';
  ctx.lineWidth = 3.5;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.bezierCurveTo(cx + 8, cy - caule * 0.3, cx - 5, cy - caule * 0.6, cx + 2, cy - caule);
  ctx.stroke();

  if (fase >= 2) {
    ctx.fillStyle = '#388e3c';
    espinho(ctx, cx + 6, cy - 15, true);
    espinho(ctx, cx - 3, cy - 30, false);
  }

  folhaDaRosa(ctx, cx + 5, cy - 10, true);
  if (fase >= 3) folhaDaRosa(ctx, cx - 5, cy - 25, false);

  const topo = { x: cx + 2, y: cy - caule };

  if (fase === 1) {
    ctx.fillStyle = '#81c784';
    circulo(ctx, topo.x, topo.y, 4);
    return;
  }
  if (fase <= 3) {
    ctx.fillStyle = '#e57373';
    oval(ctx, topo.x, topo.y, 12, 20);
    ctx.fillStyle = '#4caf50';
    oval(ctx, topo.x, topo.y + 8, 8, 6);
    return;
  }

  // A rosa aberta é feita de camadas, de fora para dentro.
  const camadas = fase === 4 ? 2 : 4;
  const base = fase === 4 ? 14 : 22;
  for (let camada = camadas - 1; camada >= 0; camada--) {
    const tamanho = base - camada * 3;
    const quantas = 5 + camada;
    const alfa = Math.min(1, 0.6 + camada * 0.1);
    ctx.fillStyle = entre([239, 83, 80], [233, 30, 99], camada / camadas, alfa);
    for (let i = 0; i < quantas; i++) {
      ctx.save();
      ctx.translate(topo.x, topo.y);
      ctx.rotate(((2 * Math.PI) / quantas) * i + camada * 0.3);
      petala(ctx, tamanho * 0.4, tamanho * 0.7, tamanho);
      ctx.restore();
    }
  }

  ctx.fillStyle = '#ffcdd2';
  circulo(ctx, topo.x, topo.y, 3);

  if (fase === 5) {
    comBrilho(ctx, 12, () => {
      ctx.fillStyle = 'rgba(233, 30, 99, 0.3)';
      circulo(ctx, topo.x, topo.y, 15);
    });
  }
};

function espinho(ctx: Pincel2D, x: number, y: number, direita: boolean) {
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(direita ? x + 6 : x - 6, y - 3);
  ctx.lineTo(x, y - 6);
  ctx.closePath();
  ctx.fill();
}

function folhaDaRosa(ctx: Pincel2D, x: number, y: number, direita: boolean) {
  ctx.fillStyle = '#66bb6a';
  ctx.beginPath();
  ctx.moveTo(x, y);
  if (direita) ctx.quadraticCurveTo(x + 18, y - 8, x + 5, y - 18);
  else ctx.quadraticCurveTo(x - 18, y - 8, x - 5, y - 18);
  ctx.closePath();
  ctx.fill();
}

/* ── Girassol (sunflower_painter.dart) ──────────────────────────────────── */

const girassol: Pincel = (ctx, lado, fase, respiro) => {
  const cx = lado / 2;
  const cy = lado * 0.8;

  vaso(ctx, cx, cy, respiro, 60, 40, '#a1887f', '#8d6e63', '#795548');
  if (fase < 1) return;

  const caule = fase * 22 + respiro * 5;
  ctx.strokeStyle = '#558b2f';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.quadraticCurveTo(cx + 5, cy - caule / 2, cx, cy - caule);
  ctx.stroke();

  ctx.fillStyle = '#7cb342';
  folhaDoGirassol(ctx, cx + 6, cy - 12, true);
  if (fase >= 2) folhaDoGirassol(ctx, cx - 6, cy - 28, false);
  if (fase >= 3) folhaDoGirassol(ctx, cx + 5, cy - 45, true);

  const topo = cy - caule;

  if (fase === 1) {
    ctx.fillStyle = '#81c784';
    circulo(ctx, cx, topo, 5);
    return;
  }
  if (fase <= 3) {
    ctx.fillStyle = '#9e9d24';
    circulo(ctx, cx, topo, fase === 2 ? 8 : 12);
    ctx.fillStyle = '#558b2f';
    for (let i = 0; i < 4; i++) {
      ctx.save();
      ctx.translate(cx, topo);
      ctx.rotate((Math.PI / 2) * i);
      petala(ctx, 5, 10, 14);
      ctx.restore();
    }
    return;
  }

  const quantas = fase === 4 ? 10 : 16;
  const largura = fase === 4 ? 16 : 24;
  ctx.fillStyle = '#ffd54f';
  for (let i = 0; i < quantas; i++) {
    ctx.save();
    ctx.translate(cx, topo);
    ctx.rotate(((2 * Math.PI) / quantas) * i);
    petala(ctx, largura * 0.3, largura * 0.6, largura);
    ctx.restore();
  }

  const miolo = fase === 4 ? 10 : 14;
  ctx.fillStyle = '#5d4037';
  circulo(ctx, cx, topo, miolo);

  ctx.fillStyle = '#795548';
  for (let i = 0; i < 8; i++) {
    const angulo = ((2 * Math.PI) / 8) * i;
    circulo(ctx, cx + Math.cos(angulo) * miolo * 0.5, topo + Math.sin(angulo) * miolo * 0.5, 1.5);
  }

  if (fase === 5) {
    comBrilho(ctx, 15, () => {
      ctx.fillStyle = 'rgba(255, 213, 79, 0.3)';
      circulo(ctx, cx, topo, 20);
    });
  }
};

function folhaDoGirassol(ctx: Pincel2D, x: number, y: number, direita: boolean) {
  ctx.beginPath();
  ctx.moveTo(x, y);
  if (direita) ctx.quadraticCurveTo(x + 22, y - 12, x + 6, y - 22);
  else ctx.quadraticCurveTo(x - 22, y - 12, x - 6, y - 22);
  ctx.closePath();
  ctx.fill();
}

/* ── Tulipa (tulip_painter.dart) ────────────────────────────────────────── */

const tulipa: Pincel = (ctx, lado, fase, respiro) => {
  const cx = lado / 2;
  const cy = lado * 0.8;

  vaso(ctx, cx, cy, respiro, 55, 38, '#b3e5fc', '#81d4fa', '#4fc3f7');
  if (fase < 1) return;

  const caule = fase * 20 + respiro * 4;
  ctx.strokeStyle = '#66bb6a';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.quadraticCurveTo(cx + 3, cy - caule / 2, cx, cy - caule);
  ctx.stroke();

  ctx.fillStyle = '#81c784';
  folhaDaTulipa(ctx, cx + 3, cy - 8, true);
  if (fase >= 2) folhaDaTulipa(ctx, cx - 3, cy - 22, false);

  const topo = cy - caule;

  if (fase === 1) {
    ctx.fillStyle = '#81c784';
    circulo(ctx, cx, topo, 4);
    return;
  }
  if (fase <= 3) {
    ctx.fillStyle = '#e91e63';
    oval(ctx, cx, topo, 10, 18 + fase * 2);
    return;
  }

  const larguraCopo = fase === 4 ? 18 : 26;
  const alturaCopo = fase === 4 ? 22 : 30;

  ctx.fillStyle = '#c2185b';
  ctx.beginPath();
  ctx.moveTo(cx - larguraCopo / 2, topo);
  ctx.quadraticCurveTo(cx - larguraCopo * 0.6, topo - alturaCopo * 0.7, cx, topo - alturaCopo);
  ctx.quadraticCurveTo(
    cx + larguraCopo * 0.6,
    topo - alturaCopo * 0.7,
    cx + larguraCopo / 2,
    topo,
  );
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#e91e63';
  ctx.beginPath();
  ctx.moveTo(cx - larguraCopo * 0.4, topo);
  ctx.quadraticCurveTo(
    cx - larguraCopo * 0.3,
    topo - alturaCopo * 0.5,
    cx,
    topo - alturaCopo * 0.8,
  );
  ctx.quadraticCurveTo(
    cx + larguraCopo * 0.3,
    topo - alturaCopo * 0.5,
    cx + larguraCopo * 0.4,
    topo,
  );
  ctx.closePath();
  ctx.fill();

  if (fase === 5) {
    comBrilho(ctx, 12, () => {
      ctx.fillStyle = 'rgba(233, 30, 99, 0.25)';
      circulo(ctx, cx, topo - alturaCopo / 2, 18);
    });
  }
};

function folhaDaTulipa(ctx: Pincel2D, x: number, y: number, direita: boolean) {
  ctx.beginPath();
  ctx.moveTo(x, y);
  if (direita) ctx.quadraticCurveTo(x + 15, y - 15, x + 3, y - 28);
  else ctx.quadraticCurveTo(x - 15, y - 15, x - 3, y - 28);
  ctx.quadraticCurveTo(direita ? x + 2 : x - 2, y - 15, x, y);
  ctx.closePath();
  ctx.fill();
}

/* ── Cerejeira (sakura_painter.dart) ────────────────────────────────────── */

const cerejeira: Pincel = (ctx, lado, fase, respiro) => {
  const cx = lado / 2;
  const cy = lado * 0.8;

  vaso(ctx, cx, cy, respiro, 65, 40, '#d7ccc8', '#bcaaa4', '#8d6e63');
  if (fase < 1) return;

  const tronco = fase * 18 + respiro * 4;

  ctx.strokeStyle = '#5d4037';
  ctx.lineCap = 'round';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.bezierCurveTo(cx + 8, cy - tronco * 0.3, cx - 5, cy - tronco * 0.6, cx + 3, cy - tronco);
  ctx.stroke();

  const forquilha = { x: cx + 3, y: cy - tronco };

  if (fase >= 2) {
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(forquilha.x, forquilha.y);
    ctx.quadraticCurveTo(
      forquilha.x + 20,
      forquilha.y - 10,
      forquilha.x + 30,
      forquilha.y - 20,
    );
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(forquilha.x, forquilha.y);
    ctx.quadraticCurveTo(
      forquilha.x - 18,
      forquilha.y - 12,
      forquilha.x - 28,
      forquilha.y - 18,
    );
    ctx.stroke();

    if (fase >= 3) {
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(forquilha.x + 15, forquilha.y - 10);
      ctx.quadraticCurveTo(
        forquilha.x + 25,
        forquilha.y - 25,
        forquilha.x + 22,
        forquilha.y - 35,
      );
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(forquilha.x - 12, forquilha.y - 10);
      ctx.quadraticCurveTo(
        forquilha.x - 22,
        forquilha.y - 22,
        forquilha.x - 20,
        forquilha.y - 32,
      );
      ctx.stroke();
    }
  }
  ctx.lineCap = 'butt';

  if (fase < 3) return;

  const flores: Array<[number, number]> = [
    [0, 0],
    [28, -18],
    [-26, -16],
  ];
  if (fase >= 4) flores.push([20, -33], [-18, -30]);
  if (fase >= 5) flores.push([0, -10], [15, -5], [-12, -8]);

  for (const [dx, dy] of flores) {
    florDeCerejeira(ctx, forquilha.x + dx, forquilha.y + dy, fase >= 5 ? 8 : 6);
  }

  if (fase === 5) {
    comBrilho(ctx, 20, () => {
      ctx.fillStyle = 'rgba(248, 187, 208, 0.2)';
      circulo(ctx, forquilha.x + 5, forquilha.y - 15, 35);
    });
  }
};

function florDeCerejeira(ctx: Pincel2D, x: number, y: number, tamanho: number) {
  ctx.fillStyle = '#f8bbd0';
  for (let i = 0; i < 5; i++) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(((2 * Math.PI) / 5) * i);
    petala(ctx, tamanho * 0.4, tamanho * 0.6, tamanho);
    ctx.restore();
  }
  ctx.fillStyle = '#e91e63';
  circulo(ctx, x, y, 2);
}

export const PINCEIS: Record<string, Pincel> = {
  lily: lirio,
  rose: rosa,
  sunflower: girassol,
  tulip: tulipa,
  sakura: cerejeira,
};

/** O `AnimationController` do `garden_plant_view.dart`: 3 s para cada lado. */
export const FOLEGO = 3000;

/**
 * O respiro no meio do fôlego, que é o que a planta parada mostra: nem no
 * começo nem no fim, a flor do meio do movimento.
 */
export const RESPIRO_PARADO = 0.5;
